import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import {
  Archive,
  Calendar,
  ClipboardList,
  LayoutDashboard,
  Settings,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import {
  buildMobileBottomNavItems,
  buildMobileManageItems,
  isMobileManagePathActive,
  type MobileBottomNavItem,
} from '../leagueNav';
import { withPreviewQuery } from '../stream/streamPages';
import LscMobileManageSheet from './LscMobileManageSheet';

const ICONS: Record<MobileBottomNavItem['iconKey'], LucideIcon> = {
  home: LayoutDashboard,
  archive: Archive,
  calendar: Calendar,
  members: Users,
  settings: Settings,
  review: ClipboardList,
  attention: ClipboardList,
  tools: Wrench,
};

function navItemClass(isActive: boolean): string {
  return [
    'flex flex-1 flex-col items-center justify-center gap-0.5 px-1 py-2.5 text-[10px] font-medium transition-colors min-w-0 min-h-11',
    isActive ? 'text-primary-300' : 'text-secondary-400 hover:text-secondary-200',
  ].join(' ');
}

interface LscMobileBottomNavProps {
  leagueId: string;
  leagueBase: string;
  leagueName?: string;
  isCommissioner: boolean;
  archived?: boolean;
  preservePreview?: boolean;
}

const LscMobileBottomNav: React.FC<LscMobileBottomNavProps> = ({
  leagueId,
  leagueBase,
  leagueName,
  isCommissioner,
  archived = false,
  preservePreview = false,
}) => {
  const location = useLocation();
  const [manageOpen, setManageOpen] = useState(false);
  const items = buildMobileBottomNavItems(leagueBase, isCommissioner, archived);
  const manageItems = buildMobileManageItems(leagueBase, archived);
  const manageActive = isCommissioner && isMobileManagePathActive(location.pathname, leagueId);

  return (
    <>
      <nav
        className="fixed inset-x-0 bottom-0 z-40 border-t border-secondary-700 bg-secondary-900/95 backdrop-blur md:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        aria-label="League mobile navigation"
        data-testid="lsc-mobile-bottom-nav"
      >
        <div className="flex items-stretch justify-around">
          {items.map(({ to, label, iconKey, end, opensManageSheet }) => {
            const Icon = ICONS[iconKey];
            const testId = `lsc-mobile-nav-${label.toLowerCase().replace(/\s+/g, '-')}`;

            if (opensManageSheet) {
              return (
                <button
                  key={label}
                  type="button"
                  className={navItemClass(manageActive)}
                  onClick={() => setManageOpen(true)}
                  aria-expanded={manageOpen}
                  aria-controls="lsc-mobile-manage-sheet"
                  data-testid={testId}
                >
                  <Icon className="h-5 w-5 shrink-0" aria-hidden />
                  <span className="truncate max-w-full">{label}</span>
                </button>
              );
            }

            return (
              <NavLink
                key={`${to}-${label}`}
                to={preservePreview && to ? withPreviewQuery(to, location.search) : to!}
                end={end}
                className={({ isActive }) => navItemClass(isActive)}
                data-testid={testId}
              >
                <Icon className="h-5 w-5 shrink-0" aria-hidden />
                <span className="truncate max-w-full">{label}</span>
              </NavLink>
            );
          })}
        </div>
        <span className="sr-only" data-testid="lsc-mobile-nav-labels">
          {items.map((item) => item.label).join(',')}
        </span>
      </nav>

      {isCommissioner ? (
        <LscMobileManageSheet
          open={manageOpen}
          leagueName={leagueName}
          items={manageItems}
          onClose={() => setManageOpen(false)}
        />
      ) : null}
    </>
  );
};

export default LscMobileBottomNav;
